import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { IdPostCampaignDto, PostCampaignDto, StageChangeStatusDto } from '@/services/ws-post/src/main/resources/rest-api';
import { getStageChangeResult, getStageChangeStatus, StageChangeStatus } from '@/services/ws-post/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { ErrorCode } from 'src/types.ts';
import { useMailingPositionErrorsNotifier } from './useMailingPositionErrorsNotification.ts';

const STAGE_CHANGE_POLLING_INTERVAL_MS = 3000;
/**
 * A failed status poll says nothing about the stage change, which keeps running on the backend - reporting the first
 * one as a failure would fail a booking that is about to succeed. Only a run of failed polls is reported.
 */
const MAX_FAILED_POLLS_IN_A_ROW = 3;
/**
 * A stage change calls Promopost several times, each with a 3 min read timeout, so a hung call chain stays PENDING far
 * longer than anyone watches a spinner.
 */
const STAGE_CHANGE_DEADLINE_MS = 5 * 60 * 1000;
const POLLING_IGNORED_ERRORS = [ErrorCode.NotFound];

type WaitForStageChange = (campaignId: IdPostCampaignDto) => Promise<PostCampaignDto | undefined>;

/**
 * Waits for the stage change that `PUT /post-campaigns/{id}` and `PATCH /post-campaigns/{campaignId}/accept` run in the
 * background when they answer 202: accepting or booking a campaign calls several Promopost APIs and can take longer
 * than the 100 s after which Cloudflare cuts the request.
 *
 * One wait at a time per component - the returned function keeps a single timer, and a second wait started while the
 * first one is still running would leave that one hanging on unmount. Both callers disable their button while waiting.
 * @returns A function resolving with the campaign the stage change produced, or `undefined` when it failed, gave up,
 * could not read the result of a finished change, or the component unmounted. Every outcome but an unmount is notified
 * about.
 */
export const useWaitForStageChange = (): WaitForStageChange => {
  const [t] = useTranslation(['backend', 'common', 'postCampaign']);
  const notify = useNotification();
  const notifyMailingPositionErrors = useMailingPositionErrorsNotifier();
  const mountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const resolveWaitRef = useRef<(() => void) | null>(null);

  const statusRequest = useLazyRequest({
    apiMethod: getStageChangeStatus,
    ignoreErrors: POLLING_IGNORED_ERRORS,
    cancelPreviousRequests: false,
  });

  const resultRequest = useLazyRequest({
    apiMethod: getStageChangeResult,
    ignoreErrors: POLLING_IGNORED_ERRORS,
    cancelPreviousRequests: false,
  });

  useEffect(
    () => () => {
      mountedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // the cleared timer would never resolve the wait, leaving the caller of the hook hanging
      resolveWaitRef.current?.();
    },
    [],
  );

  const notifyStageChangeFailure = useCallbackRef(({ translationKey, mailingPositionErrors }: StageChangeStatusDto) => {
    if (mailingPositionErrors?.length) {
      notifyMailingPositionErrors(mailingPositionErrors);
      return;
    }

    const body = translationKey ? t(`backend:${translationKey}`) : t('common:UNKNOWN_ERROR');
    notify.error({ title: t('common:ERROR'), body });
  });

  const waitForStageChange = useCallbackRef<WaitForStageChange>(async (campaignId) => {
    const deadline = Date.now() + STAGE_CHANGE_DEADLINE_MS;

    const wait = (delayMs: number) =>
      new Promise<void>((resolve) => {
        resolveWaitRef.current = resolve;
        timeoutRef.current = setTimeout(resolve, delayMs);
      });

    // false once the component is gone, so neither a notification nor a navigation reaches the user who left
    const waitForNextPoll = async () => {
      await wait(STAGE_CHANGE_POLLING_INTERVAL_MS);
      return mountedRef.current;
    };

    const poll = async (failedPollsInARow: number): Promise<PostCampaignDto | undefined> => {
      let statusDto: StageChangeStatusDto;
      const isLastAllowedPoll = failedPollsInARow >= MAX_FAILED_POLLS_IN_A_ROW;

      try {
        // a retried poll stays silent, the last allowed one reports its failure the way every request does
        ({ data: statusDto } = await statusRequest.load({ campaignId }, { silent: !isLastAllowedPoll }));
      } catch (error) {
        if (isLastAllowedPoll) {
          throw error;
        }

        return (await waitForNextPoll()) ? poll(failedPollsInARow + 1) : undefined;
      }

      if (!mountedRef.current) {
        return undefined;
      }

      if (statusDto.status === StageChangeStatus.PENDING) {
        if (Date.now() >= deadline) {
          notify.info({ body: t('postCampaign:STAGE_CHANGE_STILL_RUNNING') });
          return undefined;
        }

        return (await waitForNextPoll()) ? poll(0) : undefined;
      }

      if (statusDto.status === StageChangeStatus.ERROR) {
        notifyStageChangeFailure(statusDto);
        return undefined;
      }

      try {
        const { data } = await resultRequest.load({ campaignId }, { silent: true });
        return mountedRef.current ? data : undefined;
      } catch {
        // the stage change is done, only its stored result is gone - a failure here is not a failed booking
        if (mountedRef.current) {
          notify.info({ body: t('postCampaign:STAGE_CHANGE_RESULT_UNAVAILABLE') });
        }

        return undefined;
      }
    };

    return (await waitForNextPoll()) ? poll(0) : undefined;
  });

  return waitForStageChange;
};
